import { Button, pantryColors, pantryTypography } from '@pantry/ui'
import { Formik, useFormikContext } from 'formik'
import React, { useMemo } from 'react'
import * as Yup from 'yup'
import type { AppleWalletPassConfigInput } from '../../../../types/loyalty'
import { UnsavedChangesPanel } from '../../../Location/components/common'
import {
  useAppleWalletPassConfigQuery,
  useUpsertAppleWalletPassConfig,
} from '../../hooks/useAppleWalletPassConfig'
import { LoadingSpinner } from '../shared/LoadingSpinner'
import { AppleWalletDetailsCard } from './wallet/AppleWalletDetailsCard'
import { AppleWalletImagesCard } from './wallet/AppleWalletImagesCard'
import { exactDimensions, maxDimensions } from './wallet/imageDimensions'

const p = pantryColors
const F = pantryTypography.fontFamily

const EMPTY_CONFIG: AppleWalletPassConfigInput = {
  icon_url: '',
  icon_2x_url: '',
  icon_3x_url: '',
  logo_url: '',
  logo_2x_url: '',
  logo_3x_url: '',
  logo_text: '',
  organization_name: '',
  description: '',
}

const urlField = (label: string) =>
  Yup.string()
    .trim()
    .required(`${label} is required`)
    .url(`${label} must be a valid URL`)

const textField = (label: string) =>
  Yup.string().trim().required(`${label} is required`)

// Apple Wallet rejects pass bundles whose image assets don't match the
// documented pixel grid, so surface the same constraint client-side.
const exactImageTest = (label: string, width: number, height: number) =>
  urlField(label).test(
    `${label}-dimensions`,
    `${label} must be exactly ${width}×${height}`,
    async function exact(value) {
      const reason = await exactDimensions(value, { width, height })
      if (reason === null) return true
      return this.createError({ message: reason })
    },
  )

const maxImageTest = (label: string, width: number, height: number) =>
  urlField(label).test(
    `${label}-max-dimensions`,
    `${label} must be at most ${width}×${height}`,
    async function max(value) {
      const reason = await maxDimensions(value, { width, height })
      if (reason === null) return true
      return this.createError({ message: reason })
    },
  )

const walletSchema = Yup.object({
  icon_url: exactImageTest('Icon @1x', 29, 29),
  icon_2x_url: exactImageTest('Icon @2x', 58, 58),
  icon_3x_url: exactImageTest('Icon @3x', 87, 87),
  logo_url: maxImageTest('Logo @1x', 160, 50),
  logo_2x_url: maxImageTest('Logo @2x', 320, 100),
  logo_3x_url: maxImageTest('Logo @3x', 480, 150),
  logo_text: textField('Logo text'),
  organization_name: textField('Organization name'),
  description: textField('Description'),
})

const WalletFormBody: React.FC<{ isSaving: boolean }> = ({ isSaving }) => {
  const { dirty, resetForm, submitForm, isValid } =
    useFormikContext<AppleWalletPassConfigInput>()

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        fontFamily: F,
        // UnsavedChangesPanel is `position: fixed; bottom: 16` and ~52px tall,
        // so without this reservation the panel slides up and covers the last
        // card's footer when the form is dirty. Always-on padding keeps the
        // scroll height stable (no layout shift when the panel toggles in/out).
        paddingBottom: 96,
      }}
    >
      {/*
        Stacked vertically: Details on top, Images below. Both cards span the
        full form width so each section gets the breathing room it needs
        (Details for long text fields, Images for the six tile columns).
      */}
      <AppleWalletDetailsCard />
      <AppleWalletImagesCard />
      <UnsavedChangesPanel
        isVisible={dirty}
        isSaving={isSaving}
        isInvalid={!isValid}
        saveLabel="Save Wallet Config"
        onSave={() => submitForm()}
        onDiscard={() => resetForm()}
      />
    </div>
  )
}

interface WalletTabProps {
  storeId: string
}

export const WalletTab: React.FC<WalletTabProps> = ({ storeId }) => {
  const { data, isLoading, isError, refetch } =
    useAppleWalletPassConfigQuery(storeId)
  const mutation = useUpsertAppleWalletPassConfig(storeId)

  // Map a loaded config doc back into the input shape (strip _id / _created /
  // _updated / store_id). When `data` is null (no config saved yet) seed with
  // an empty document so the form renders all 9 inputs straight away.
  const initialValues: AppleWalletPassConfigInput = useMemo(() => {
    if (!data) return EMPTY_CONFIG
    return {
      icon_url: data.icon_url ?? '',
      icon_2x_url: data.icon_2x_url ?? '',
      icon_3x_url: data.icon_3x_url ?? '',
      logo_url: data.logo_url ?? '',
      logo_2x_url: data.logo_2x_url ?? '',
      logo_3x_url: data.logo_3x_url ?? '',
      logo_text: data.logo_text ?? '',
      organization_name: data.organization_name ?? '',
      description: data.description ?? '',
    }
  }, [data])

  if (isLoading) {
    return (
      <div style={{ fontFamily: F }}>
        <LoadingSpinner label="Loading wallet config…" />
      </div>
    )
  }

  if (isError) {
    return (
      <div
        style={{
          background: p.white,
          border: `1px solid ${p.gray[200]}`,
          borderRadius: 10,
          padding: '40px 24px',
          textAlign: 'center',
          fontFamily: F,
        }}
      >
        <div
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: p.gray[700],
            marginBottom: 6,
          }}
        >
          Couldn't load Apple Wallet config
        </div>
        <div style={{ fontSize: 13, color: p.gray[500], marginBottom: 16 }}>
          Something went wrong while fetching the wallet configuration.
        </div>
        <Button variant="ghost" onClick={() => refetch()}>
          Try again
        </Button>
      </div>
    )
  }

  return (
    <Formik<AppleWalletPassConfigInput>
      initialValues={initialValues}
      enableReinitialize
      validationSchema={walletSchema}
      onSubmit={async (values, helpers) => {
        try {
          await mutation.mutateAsync(values)
          helpers.resetForm({ values })
        } catch {
          // Error surfacing is left to the host app; keep the form dirty so
          // the user can retry without re-typing.
        }
      }}
    >
      <WalletFormBody isSaving={mutation.isPending} />
    </Formik>
  )
}
